/**
 * Column filter types for the V1 search-results experience.
 *
 * Each filterable column maps to ONE of these types. The ColumnFilterPopover
 * dispatches to the right Filter component (NumericRangeFilter,
 * KeywordCloudFilter, MultiSelectFilter, LocationFilter) based on this map.
 *
 * Keys must match the corresponding entries in PROFILER_SEARCH_RESULTS_COLUMNS
 * (constants/constants.js) so the registry stays consistent.
 */

export const COLUMN_FILTER_TYPES = Object.freeze({
  NUMERIC: 'numeric', // range slider (min/max)
  KEYWORD_CLOUD: 'keyword-cloud', // typeahead + top-10 frequency + AND/OR + quoted-exact
  CONTROLLED: 'controlled', // multi-select checkbox list with counts
  LOCATION: 'location' // split: Country checkbox + City substring (single popover)
});

/**
 * Map: column key (matches PROFILER_SEARCH_RESULTS_COLUMNS values) → filter type.
 *
 * Columns NOT in this map are not filterable from their header (e.g. Company name
 * in V1 — analysts use the top-level search).
 *
 * NOTE: the keys here MUST match the actual data-field names on the company object,
 * not the column-config IDs, because the filter engine reads `company[key]`.
 */
export const COLUMN_FILTER_TYPE_BY_KEY = Object.freeze({
  // numeric
  revenue: COLUMN_FILTER_TYPES.NUMERIC,
  employees: COLUMN_FILTER_TYPES.NUMERIC,
  ebitPercentage: COLUMN_FILTER_TYPES.NUMERIC,
  cagr3Y: COLUMN_FILTER_TYPES.NUMERIC,

  // controlled list (situation is an array of {name} per the data model,
  // but the values are drawn from a small enumerated set; the popover shows
  // checkboxes rather than a typeahead)
  situation: COLUMN_FILTER_TYPES.CONTROLLED,
  // `registerStatus` is the English name in a one-item array, derived from
  // `companyStatus` in mapCompanyEntry ("Unknown" when the status is not known). It has no
  // list column; the filter is offered from the Add filter panel only.
  registerStatus: COLUMN_FILTER_TYPES.CONTROLLED,

  // location is a combined "City, Country" string field in `headquarters`
  headquarters: COLUMN_FILTER_TYPES.LOCATION,

  // TD-2456 — Industry / Value chain / Product portfolio are now filterable via
  // clickable pills (see CompanyResult → ChipsList → state.togglePillFilter).
  // Registering them here activates `matchKeywordCloud` in the engine so a
  // clicked pill narrows the list to companies sharing that value; multiple
  // clicked pills combine per the filter's match mode, which starts at AND
  // ("All") today and at OR ("Any") under the unified-filter flag — see
  // `defaultKeywordMode` in pillFilters. A companion backend endpoint
  // (TD-2453) later widens a single clicked value to its semantically-similar
  // variants at click time.
  // NOTE: Key end-markets (businessVerticals) stays deferred — not in scope for
  // TD-2456, and no clickable-pill affordance is wired for it yet.
  industries: COLUMN_FILTER_TYPES.KEYWORD_CLOUD,
  valueChain: COLUMN_FILTER_TYPES.KEYWORD_CLOUD,
  products: COLUMN_FILTER_TYPES.KEYWORD_CLOUD
});

/**
 * TD-2456 — the keyword-cloud columns driven by clickable pills, derived from
 * the type map so it stays in sync if more are added/removed. Used to scope the
 * pill-filter chip rail to just these columns.
 */
export const KEYWORD_CLOUD_COLUMNS = Object.freeze(
  Object.keys(COLUMN_FILTER_TYPE_BY_KEY).filter(
    (key) => COLUMN_FILTER_TYPE_BY_KEY[key] === COLUMN_FILTER_TYPES.KEYWORD_CLOUD
  )
);

/**
 * Display label for a column key when shown in filter chips.
 */
export const COLUMN_LABEL_BY_KEY = Object.freeze({
  revenue: 'Sales',
  employees: 'Employees',
  ebitPercentage: 'EBIT Margin',
  cagr3Y: 'CAGR 3Y',
  industries: 'Industry',
  valueChain: 'Value chain',
  products: 'Products',
  businessVerticals: 'Key end-markets',
  situation: 'Situation',
  registerStatus: 'Register status',
  headquarters: 'Location'
});

/**
 * Format a numeric value with the right unit/precision for the chip label
 * and the popover help text. Keep tight to mirror the prototype's chip-style.
 */
export const NUMERIC_UNIT_BY_KEY = Object.freeze({
  revenue: '€M',
  employees: '',
  ebitPercentage: '%',
  cagr3Y: '%'
});

/**
 * For numeric columns, the actual numeric value lives on an underscore-prefixed
 * field on each company entry (the display field is a pre-formatted string like
 * "8.3%" or "37.2'24"). The non-prefixed name is what the column header /
 * filter chip / user-facing API uses; the prefixed name is what the engine
 * reads for comparisons. Same convention is used by
 * `profilerSearchSortOptions.js` (e.g. `_revenue-asc`, `_cagr3Y-desc`).
 */
export const NUMERIC_DATA_FIELD_BY_KEY = Object.freeze({
  revenue: '_revenue',
  employees: '_employees',
  ebitPercentage: '_ebitPercentage',
  cagr3Y: '_cagr3Y'
});

/**
 * Resolve the actual data-access field name for a column key — uses the
 * underscore-prefixed numeric field for known numeric keys, otherwise returns
 * the key unchanged (so categorical / text fields keep their display name).
 */
export function dataFieldForKey(columnKey) {
  return NUMERIC_DATA_FIELD_BY_KEY[columnKey] ?? columnKey;
}

/**
 * Per-column display scale. Some fields are stored as raw values but rendered
 * to analysts in a different unit; the popover always shows / accepts display
 * units, the engine always compares raw values. Conversion lives in the UI
 * boundary via `formatNumericForChip` and `FilterNumericRange`.
 *
 *   - `revenue` (`_revenue`) is stored as raw euros (e.g. 40_000_000) but
 *     the row column displays it divided by 1 M (e.g. "42.3").
 *
 *   - `cagr3Y` / `cagr4Y` / `ebitPercentage` come back from the BE as
 *     **fractional** numbers (e.g. `0.056` = 5.6%; sample BE response in
 *     V1.5 plan). Without a scale, the popover would render `0.056` as
 *     `0.1%` and a user-typed `10` (intending 10%) would filter rows with
 *     `_cagr3Y >= 10` — excluding everything. Scale `0.01` means
 *     `displayValue = raw / 0.01 = raw × 100`, so 0.056 displays as `5.6%`
 *     and a typed `10` is stored as `0.1` to match the fractional storage.
 *
 * For any column not listed here the scale is 1 (display value == stored
 * value), so existing semantics for Employees are untouched.
 */
export const NUMERIC_DISPLAY_SCALE_BY_KEY = Object.freeze({
  revenue: 1_000_000,
  cagr3Y: 0.01,
  cagr4Y: 0.01,
  ebitPercentage: 0.01
});

export function displayScale(columnKey) {
  return NUMERIC_DISPLAY_SCALE_BY_KEY[columnKey] ?? 1;
}

// All numeric chip / popover labels use US locale (comma thousands, dot
// decimal) for consistency with the rest of the platform regardless of the
// user's browser locale.
const US_LOCALE = 'en-US';

export function formatNumericForChip(key, value) {
  if (value == null) return '—';
  // The API sometimes returns numeric fields as strings (e.g. "37.2"). Coerce
  // safely; reject empty / whitespace-only strings (which Number() turns into
  // 0) and any value that doesn't end up finite.
  if (typeof value === 'string' && value.trim() === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';

  // Divide by the per-column display scale so values stored in raw units
  // (e.g. raw euros for `revenue`) are shown in the displayed unit (millions).
  const displayValue = n / displayScale(key);

  const unit = NUMERIC_UNIT_BY_KEY[key] ?? '';
  if (key === 'employees') {
    // Integer-friendly: e.g. 1234 → "1,234"
    return displayValue.toLocaleString(US_LOCALE);
  }
  // Sales / margin / cagr — one decimal place, US thousands separator:
  // e.g. 1234.5 → "1,234.5"
  const formatted = displayValue.toLocaleString(US_LOCALE, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
  return `${formatted}${unit}`;
}

export function isFilterableColumn(key) {
  return Object.hasOwn(COLUMN_FILTER_TYPE_BY_KEY, key);
}
